import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * @module useMonthlyReports
 * @description Custom hook for fetching monthly reports in the Mental Health Portal application.
 * Supports filtering, pagination, and automatically refetches when parameters change.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to fetch and manage monthly reports with filtering and pagination
 * @function useMonthlyReports
 * @param {Object} options - Configuration options for fetching reports
 * @param {string|boolean} [options.edited] - Filter by edited status ('true', 'false', or undefined)
 * @param {boolean} [options.all] - Whether to fetch all reports regardless of filters
 * @param {number} [options.page=1] - Current page number for pagination
 * @param {number} [options.pageSize=20] - Number of reports per page
 * @returns {Object} Object containing data, loading state, and error state
 */
export const useMonthlyReports = ({
  edited,
  all,
  page = 1,
  pageSize = 20,
} = {}) => {
  /**
   * @state {Object} data - The fetched reports data
   * @property {Array} reports - List of monthly reports
   * @property {number} total - Total count of reports matching the filter
   */
  const [data, setData] = useState({ reports: [], total: 0 });

  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(true);

  /**
   * @state {string|null} error - Error message if the API request fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Effect hook to fetch reports when parameters change
   * @effect
   * @param {string|boolean} edited - Filter by edited status
   * @param {boolean} all - Whether to fetch all reports
   * @param {number} page - Current page number
   * @param {number} pageSize - Number of reports per page
   */
  useEffect(() => {
    /**
     * Fetches monthly reports from the API with the specified filters and pagination
     * @async
     * @function fetchReports
     * @returns {Promise<void>}
     */
    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        if (edited !== undefined) params.append('edited', edited);
        if (all !== undefined) params.append('all', all);
        params.append('limit', pageSize);
        params.append('offset', (page - 1) * pageSize);

        const { data } = await api.get(
          `list-monthly-reports?${params.toString()}`
        );
        setData(data);
      } catch (err) {
        setError('Erro ao buscar relatórios mensais');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [edited, all, page, pageSize]);

  /**
   * @typedef {Object} UseMonthlyReportsReturn
   * @property {Object} data - The fetched reports data
   * @property {Array} data.reports - List of monthly reports
   * @property {number} data.total - Total count of reports matching the filter
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string|null} error - Error message if the API request fails, null otherwise
   */

  /**
   * @returns {UseMonthlyReportsReturn} The hook's return values
   */
  return { data, loading, error };
};
